import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  FormArray,
  FormControlDirective,
  FormControlName,
  NgModel,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Observable } from 'rxjs';

export function emptyValue<T>() {
  return '' as unknown as T;
}

export type NgControlDirective = FormControlDirective | FormControlName | NgModel;

export type TypedValidatorFn<TControl extends AbstractControl> = (
  control: TControl,
) => ValidationErrors | null;
export type TypedAsyncValidatorFn<TControl extends AbstractControl> = (
  control: TControl,
) => Promise<ValidationErrors | null> | Observable<ValidationErrors | null>;

export interface TypedAbstractControlOptions<TControl extends AbstractControl> {
  validators?: TypedValidatorFn<TControl> | Array<TypedValidatorFn<TControl>> | null;
  asyncValidators?: TypedAsyncValidatorFn<TControl> | Array<TypedAsyncValidatorFn<TControl>> | null;
  updateOn?: 'change' | 'blur' | 'submit';
}

export type TypedFormBuilderControlArgs<T> =
  | [
      T | { value: T; disabled: boolean },
      TypedValidatorFn<TypedFormControl<T>> | Array<TypedValidatorFn<TypedFormControl<T>>> | null,
      (
        | TypedAsyncValidatorFn<TypedFormControl<T>>
        | Array<TypedAsyncValidatorFn<TypedFormControl<T>>>
        | null
      ),
    ]
  | [
      T | { value: T; disabled: boolean },
      (
        | TypedValidatorFn<TypedFormControl<T>>
        | Array<TypedValidatorFn<TypedFormControl<T>>>
        | TypedAbstractControlOptions<TypedFormControl<T>>
        | null
      ),
    ]
  | [T | { value: T; disabled: boolean }]
  | { value: T; disabled: boolean }
  | T;

export interface TypedFormBuilderGroupConfig {
  [key: string]: AbstractControl | TypedFormBuilderControlArgs<any>;
}

/**
 * Converts from form group config to the type argument for TypedFormGroup
 */
export type TypedFormGroupType<T> = {
  [K in keyof T]: T[K] extends AbstractControl
    ? T[K]
    : [T[K]] extends [{ value: infer Value; disabled: boolean }]
      ? TypedFormControl<Value>
      : [T[K]] extends [Array<infer Item>]
        ? [Item] extends [
            | { value: infer Value; disabled: boolean }
            | ValidatorFn
            | ValidatorFn[]
            | AsyncValidatorFn
            | AsyncValidatorFn[]
            | (AbstractControlOptions | { updateOn: string }),
          ]
          ? TypedFormControl<Value>
          : [Item] extends [
                | infer Value
                | ValidatorFn
                | ValidatorFn[]
                | AsyncValidatorFn
                | AsyncValidatorFn[]
                | (AbstractControlOptions | { updateOn: string }),
              ]
            ? TypedFormControl<Value>
            : never
        : [T[K]] extends [any]
          ? TypedFormControl<T[K]>
          : never;
};

@Injectable({
  providedIn: 'root',
})
export class TypedFormBuilder extends UntypedFormBuilder {
  group<T extends TypedFormBuilderGroupConfig>(
    controlsConfig: T,
    options?: TypedAbstractControlOptions<TypedFormGroup<TypedFormGroupType<T>>> | null,
  ) {
    return super.group(controlsConfig, options as AbstractControlOptions | null) as TypedFormGroup<
      TypedFormGroupType<T>
    >;
  }

  control<T>(
    formState: T | { value: T; disabled: boolean },
    validatorOrOpts?:
      | TypedValidatorFn<TypedFormControl<T>>
      | Array<TypedValidatorFn<TypedFormControl<T>>>
      | TypedAbstractControlOptions<TypedFormControl<T>>
      | null,
    asyncValidator?:
      | TypedAsyncValidatorFn<TypedFormControl<T>>
      | Array<TypedAsyncValidatorFn<TypedFormControl<T>>>
      | null,
  ) {
    return super.control(
      formState,
      validatorOrOpts as ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
      asyncValidator as AsyncValidatorFn | AsyncValidatorFn[] | null,
    ) as TypedFormControl<T>;
  }

  array<T extends AbstractControl>(
    controlsConfig: T[],
    validatorOrOpts?:
      | TypedValidatorFn<TypedFormArray<T>>
      | Array<TypedValidatorFn<TypedFormArray<T>>>
      | TypedAbstractControlOptions<TypedFormArray<T>>
      | null,
    asyncValidator?:
      | TypedAsyncValidatorFn<TypedFormArray<T>>
      | Array<TypedAsyncValidatorFn<TypedFormArray<T>>>
      | null,
  ) {
    return super.array(
      controlsConfig,
      validatorOrOpts as ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
      asyncValidator as AsyncValidatorFn | AsyncValidatorFn[] | null,
    ) as TypedFormArray<T>;
  }
}

export interface TypedAbstractControl<T> extends AbstractControl {
  value: T;
  valueChanges: Observable<T>;
}

export interface TypedFormControl<T> extends UntypedFormControl, TypedAbstractControl<T> {
  value: T;
  valueChanges: Observable<T>;

  setValue(
    value: T,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
      emitModelToViewChange?: boolean;
      emitViewToModelChange?: boolean;
    },
  ): void;

  patchValue(
    value: T,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
      emitModelToViewChange?: boolean;
      emitViewToModelChange?: boolean;
    },
  ): void;

  reset(
    formState?: T | { value: T; disabled: boolean },
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    },
  ): void;

  setValidators(
    validators:
      | TypedValidatorFn<TypedFormControl<T>>
      | Array<TypedValidatorFn<TypedFormControl<T>>>
      | null,
  ): void;
  setAsyncValidators(
    validators:
      | TypedAsyncValidatorFn<TypedFormControl<T>>
      | Array<TypedAsyncValidatorFn<TypedFormControl<T>>>
      | null,
  ): void;
  addValidators(
    validators:
      | TypedValidatorFn<TypedFormControl<T>>
      | Array<TypedValidatorFn<TypedFormControl<T>>>
      | null,
  ): void;
  addAsyncValidators(
    validators:
      | TypedAsyncValidatorFn<TypedFormControl<T>>
      | Array<TypedAsyncValidatorFn<TypedFormControl<T>>>
      | null,
  ): void;
  removeValidators(
    validators:
      | TypedValidatorFn<TypedFormControl<T>>
      | Array<TypedValidatorFn<TypedFormControl<T>>>
      | null,
  ): void;
  removeAsyncValidators(
    validators:
      | TypedAsyncValidatorFn<TypedFormControl<T>>
      | Array<TypedAsyncValidatorFn<TypedFormControl<T>>>
      | null,
  ): void;
  hasValidator(validator: TypedValidatorFn<TypedFormControl<T>>): boolean;
  hasAsyncValidator(validator: TypedAsyncValidatorFn<TypedFormControl<T>>): boolean;
}

/**
 * Given a control "TControl", returns the type of TControl.value
 */
type TypeFromControl<TControl> =
  TControl extends TypedAbstractControl<infer Value>
    ? [Value] extends [any]
      ? Value
      : never
    : any;

export interface TypedFormArray<TControl extends AbstractControl>
  extends FormArray<any>,
    TypedAbstractControl<Array<TypeFromControl<TControl>>> {
  getRawValue(): any;

  value: Array<TypeFromControl<TControl>>;
  valueChanges: Observable<Array<TypeFromControl<TControl>>>;
  controls: {
    /** @deprecated use TypedFormArray.push instead */
    push(...args: TControl[]): number;
  } & TControl[];

  at(index: number): TControl;
  push(
    control: TControl,
    options?: {
      emitEvent?: boolean;
    },
  ): void;
  insert(index: number, control: TControl): void;
  setControl(index: number, control: TControl): void;
  setValue(
    value: Array<TypeFromControl<TControl>>,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    },
  ): void;
  patchValue(
    value: Array<TypeFromControl<TControl>>,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    },
  ): void;
  reset(
    value?: Array<TypeFromControl<TControl>>,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    },
  ): void;

  setValidators(
    validators:
      | TypedValidatorFn<TypedFormArray<TControl>>
      | Array<TypedValidatorFn<TypedFormArray<TControl>>>
      | null,
  ): void;
  setAsyncValidators(
    validators:
      | TypedAsyncValidatorFn<TypedFormArray<TControl>>
      | Array<TypedAsyncValidatorFn<TypedFormArray<TControl>>>
      | null,
  ): void;
  addValidators(
    validators:
      | TypedValidatorFn<TypedFormArray<TControl>>
      | Array<TypedValidatorFn<TypedFormArray<TControl>>>
      | null,
  ): void;
  addAsyncValidators(
    validators:
      | TypedAsyncValidatorFn<TypedFormArray<TControl>>
      | Array<TypedAsyncValidatorFn<TypedFormArray<TControl>>>
      | null,
  ): void;
  removeValidators(
    validators:
      | TypedValidatorFn<TypedFormArray<TControl>>
      | Array<TypedValidatorFn<TypedFormArray<TControl>>>
      | null,
  ): void;
  removeAsyncValidators(
    validators:
      | TypedAsyncValidatorFn<TypedFormArray<TControl>>
      | Array<TypedAsyncValidatorFn<TypedFormArray<TControl>>>
      | null,
  ): void;
  hasValidator(validator: TypedValidatorFn<TypedFormArray<TControl>>): boolean;
  hasAsyncValidator(validator: TypedAsyncValidatorFn<TypedFormArray<TControl>>): boolean;
}

export type NullPartial<T> = {
  [K in keyof T]?: NullPartial<T[K]> | null;
};

export type PatchValue<TControl> =
  | (TControl extends TypedFormGroup<infer TGroup>
      ? {
          [K in keyof TGroup]?: PatchValue<TGroup[K]>;
        }
      : TypeFromControl<TControl>)
  | null
  | undefined;

export interface TypedFormGroup<TGroup extends { [key: string]: AbstractControl }>
  extends UntypedFormGroup,
    TypedAbstractControl<{
      [K in keyof TGroup]: TypeFromControl<TGroup[K]>;
    }> {
  value: {
    [K in keyof TGroup]: TypeFromControl<TGroup[K]>;
  };
  valueChanges: Observable<{
    [K in keyof TGroup]: TypeFromControl<TGroup[K]>;
  }>;
  controls: TGroup;

  addControl<K extends keyof TGroup>(name: K, control: TGroup[K]): void;
  setValue(
    value: {
      [K in keyof TGroup]: TypeFromControl<TGroup[K]>;
    },
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    },
  ): void;
  patchValue(
    value: {
      [K in keyof TGroup]?: PatchValue<TGroup[K]>;
    },
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    },
  ): void;
  reset(
    value?: {
      [K in keyof TGroup]: TypeFromControl<TGroup[K]>;
    },
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    },
  ): void;

  setValidators(
    validators:
      | TypedValidatorFn<TypedFormGroup<TGroup>>
      | Array<TypedValidatorFn<TypedFormGroup<TGroup>>>
      | null,
  ): void;
  setAsyncValidators(
    validators:
      | TypedAsyncValidatorFn<TypedFormGroup<TGroup>>
      | Array<TypedAsyncValidatorFn<TypedFormGroup<TGroup>>>
      | null,
  ): void;
  addValidators(
    validators:
      | TypedValidatorFn<TypedFormGroup<TGroup>>
      | Array<TypedValidatorFn<TypedFormGroup<TGroup>>>
      | null,
  ): void;
  addAsyncValidators(
    validators:
      | TypedAsyncValidatorFn<TypedFormGroup<TGroup>>
      | Array<TypedAsyncValidatorFn<TypedFormGroup<TGroup>>>
      | null,
  ): void;
  removeValidators(
    validators:
      | TypedValidatorFn<TypedFormGroup<TGroup>>
      | Array<TypedValidatorFn<TypedFormGroup<TGroup>>>
      | null,
  ): void;
  removeAsyncValidators(
    validators:
      | TypedAsyncValidatorFn<TypedFormGroup<TGroup>>
      | Array<TypedAsyncValidatorFn<TypedFormGroup<TGroup>>>
      | null,
  ): void;
  hasValidator(validator: TypedValidatorFn<TypedFormGroup<TGroup>>): boolean;
  hasAsyncValidator(validator: TypedAsyncValidatorFn<TypedFormGroup<TGroup>>): boolean;
}
